/**
 * Persistent Files Service
 * Files are project-scoped. When using OAuth auth, the projectId must be provided.
 * When using API key auth, the project is inferred from the key.
 */

import type { GatewayClient } from './client'
import type { UploadProgressCallback } from './types'

// ============================================================================
// Types
// ============================================================================

export interface FileResponse {
  filename: string
  hash: string
  size: number
  mimeType: string
  fileRef: string
  folderId?: string
  createdAt: string
  inspection?: FileInspection
}

export interface FileInspection {
  contentType: string
  extension: string
  size: number
  dimensions?: {
    width: number
    height: number
  }
  pageCount?: number
  metadata?: Record<string, unknown>
}

export interface FileListResponse {
  files: FileResponse[]
  pageSize: number
  nextCursor?: string
  totalStorageBytes: number
}

export interface ListFilesOptions {
  limit?: number
  cursor?: string
  sort?: 'filename' | 'size' | 'created' | 'mime_type'
  order?: 'asc' | 'desc'
  mime?: string
  folderId?: string
  search?: string
  includeSubfolders?: boolean
}

export interface UpdateFileRequest {
  newFilename?: string
  folderId?: string
}

export interface BatchFileIdentifier {
  filename: string
  folderId?: string
}

export interface BatchDeleteRequest {
  files: BatchFileIdentifier[]
}

export interface BatchDeleteResponse {
  results: BatchOperationResult[]
  totalDeleted: number
  totalFailed: number
  bytesReclaimed: number
}

export interface BatchMoveRequest {
  files: BatchFileIdentifier[]
  targetFolderId: string
}

export interface BatchMoveResponse {
  results: BatchOperationResult[]
  totalMoved: number
  totalFailed: number
}

export interface BatchOperationResult {
  filename: string
  folderId: string
  success: boolean
  error?: string
}

export interface PromoteRequest {
  /** Ephemeral file hash to promote */
  hash: string
  /** Filename for the persistent file */
  filename: string
}

export interface DemoteRequest {
  /** Persistent file hash to demote */
  hash: string
}

export interface SignedDownloadURLResponse {
  downloadUrl: string
  fileRef: string
  expiresAt: string
  size: number
  contentType: string
}

export interface FileUploadOptions {
  onProgress?: UploadProgressCallback
  /** Target folder ID (empty string or undefined = root folder) */
  folderId?: string
}

/**
 * Options for creating a project-scoped files service
 */
export interface ProjectFilesOptions {
  /** Project ID for scoping file operations (required for OAuth auth) */
  projectId: string
}

export interface PersistentFilesService {
  /**
   * List persistent files
   */
  list(options?: ListFilesOptions): Promise<FileListResponse>

  /**
   * Upload a file to persistent storage
   */
  upload(
    file: File | Blob,
    filename: string,
    options?: FileUploadOptions
  ): Promise<FileResponse>

  /**
   * Get persistent file by hash
   */
  get(hash: string): Promise<FileResponse>

  /**
   * Update a persistent file (rename and/or move to folder)
   */
  update(hash: string, updates: UpdateFileRequest): Promise<FileResponse>

  /**
   * Delete a persistent file
   */
  delete(hash: string): Promise<void>

  /**
   * Get content URL for a persistent file
   */
  getContentUrl(hash: string): string

  /**
   * Get signed download URL for a persistent file
   */
  getDownloadUrl(hash: string): Promise<SignedDownloadURLResponse>

  /**
   * Batch delete multiple files
   */
  batchDelete(files: BatchFileIdentifier[]): Promise<BatchDeleteResponse>

  /**
   * Batch move multiple files to a folder
   */
  batchMove(
    files: BatchFileIdentifier[],
    targetFolderId: string
  ): Promise<BatchMoveResponse>

  /**
   * Promote an ephemeral file to persistent storage
   */
  promote(hash: string, filename: string): Promise<FileResponse>

  /**
   * Demote a persistent file to ephemeral storage
   */
  demote(hash: string): Promise<{ hash: string; size: number }>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a PersistentFilesService instance for a specific project.
 *
 * The projectId is required for OAuth authentication to specify which project's
 * files to access. For API key authentication, the project is inferred from
 * the key, but the projectId header is still sent for consistency.
 *
 * @param client - The GatewayClient instance
 * @param options - Options including the projectId
 */
export function createPersistentFilesService(
  client: GatewayClient,
  options: ProjectFilesOptions
): PersistentFilesService {
  const baseUrl = client.config.baseUrl
  const { projectId } = options

  // Helper to build request options with projectId query parameter
  const withProjectParam = (
    additionalOptions?: Record<string, unknown>
  ): Record<string, unknown> => ({
    ...additionalOptions,
    params: {
      projectId,
      ...(additionalOptions?.params as Record<string, string>),
    },
  })

  return {
    async list(options?: ListFilesOptions): Promise<FileListResponse> {
      return client.get<FileListResponse>(
        '/v1/files',
        withProjectParam({
          params: options as Record<
            string,
            string | number | boolean | undefined
          >,
        })
      )
    },

    async upload(
      file: File | Blob,
      filename: string,
      options?: FileUploadOptions
    ): Promise<FileResponse> {
      const fields: Record<string, string> = {
        filename,
        persist: 'true',
      }

      // Add folderId if specified
      if (options?.folderId) {
        fields.folderId = options.folderId
      }

      // Add projectId as query parameter in the URL
      const response = await client.uploadFile(
        `/v1/files?projectId=${encodeURIComponent(projectId)}`,
        file,
        fields,
        options?.onProgress
      )
      return response.json()
    },

    async get(hash: string): Promise<FileResponse> {
      return client.get<FileResponse>(`/v1/files/${hash}`, withProjectParam())
    },

    async update(
      hash: string,
      updates: UpdateFileRequest
    ): Promise<FileResponse> {
      return client.patch<FileResponse>(
        `/v1/files/${hash}`,
        updates,
        withProjectParam()
      )
    },

    async delete(hash: string): Promise<void> {
      await client.delete<void>(`/v1/files/${hash}`, withProjectParam())
    },

    getContentUrl(hash: string): string {
      return `${baseUrl}/v1/files/${hash}/content?projectId=${encodeURIComponent(projectId)}`
    },

    async getDownloadUrl(hash: string): Promise<SignedDownloadURLResponse> {
      return client.get<SignedDownloadURLResponse>(
        `/v1/files/${hash}/download-url`,
        withProjectParam()
      )
    },

    async batchDelete(
      files: BatchFileIdentifier[]
    ): Promise<BatchDeleteResponse> {
      return client.post<BatchDeleteResponse>(
        '/v1/files/batch-delete',
        { files },
        withProjectParam()
      )
    },

    async batchMove(
      files: BatchFileIdentifier[],
      targetFolderId: string
    ): Promise<BatchMoveResponse> {
      return client.post<BatchMoveResponse>(
        '/v1/files/batch-move',
        { files, targetFolderId },
        withProjectParam()
      )
    },

    async promote(hash: string, filename: string): Promise<FileResponse> {
      return client.post<FileResponse>(
        '/v1/files/promote',
        { hash, filename },
        withProjectParam()
      )
    },

    async demote(hash: string): Promise<{ hash: string; size: number }> {
      // Note: Demote endpoint is at /v1/ephemeral/demote since it produces an ephemeral file
      return client.post<{ hash: string; size: number }>(
        '/v1/ephemeral/demote',
        { hash },
        withProjectParam()
      )
    },
  }
}
